<script setup lang="ts">
import { computed, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { routes } from '@app/router/routes'
import { KYC_SUBMISSION_STATUS, useKycStatusQuery, useUserProfileQuery } from '@modules/account'
import { useDashboardState } from '@modules/dashboard'
import { fullName } from '@shared/utils'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import logo from '@assets/images/logo.png'
import slicealText from '@assets/images/sliceal-text.png'

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'logout'): void
}>()

const route = useRoute()
const profileQuery = useUserProfileQuery()
const kycStatusQuery = useKycStatusQuery()
const dashboardState = useDashboardState()

const displayName = computed(() =>
  fullName(profileQuery.data.value?.firstName, profileQuery.data.value?.lastName),
)
const displayEmail = computed(() => profileQuery.data.value?.email ?? '')
const isKycVerified = computed(
  () => kycStatusQuery.data.value?.kycStatus === KYC_SUBMISSION_STATUS.APPROVED,
)
const isGuest = computed(() => dashboardState.value.kind === 'guest')

interface NavItem {
  label: string
  to: string
  icon: PiIconName
}

const allPrimaryItems: NavItem[] = [
  { label: 'Dashboard', to: routes.DASHBOARD, icon: 'home' },
  { label: 'Properties', to: routes.PROPERTIES, icon: 'chart-bar' },
  { label: 'Portfolio', to: routes.PORTFOLIO, icon: 'briefcase' },
  { label: 'Wallet', to: routes.WALLET, icon: 'wallet' },
]

const allSecondaryItems: NavItem[] = [
  { label: 'Profile', to: routes.PROFILE, icon: 'user' },
  { label: 'My orders', to: routes.MY_ORDERS, icon: 'list-check' },
  { label: 'Bank accounts', to: routes.BANK_ACCOUNTS, icon: 'wallet' },
  { label: 'Settings', to: routes.SETTINGS, icon: 'cog' },
  { label: 'Help & support', to: routes.HELP, icon: 'comments' },
]

const primaryItems = computed(() =>
  isGuest.value
    ? allPrimaryItems.filter((item) => item.to !== routes.WALLET)
    : allPrimaryItems,
)

const secondaryItems = computed(() =>
  isGuest.value
    ? allSecondaryItems.filter(
        (item) => item.to !== routes.MY_ORDERS && item.to !== routes.BANK_ACCOUNTS,
      )
    : allSecondaryItems,
)

// Auto-close on every successful navigation.
watch(
  () => route.fullPath,
  () => emit('update:modelValue', false),
)

const close = () => emit('update:modelValue', false)
const handleLogout = () => {
  emit('update:modelValue', false)
  emit('logout')
}
</script>

<template>
  <Drawer
    :model-value="props.modelValue"
    position="left"
    width="288px"
    :show-close-icon="false"
    @update:model-value="(value: boolean) => emit('update:modelValue', value)"
  >
    <template #header>
      <Box align="center" justify="between" block class="drawer-header">
        <Box align="center" gap="2">
          <img :src="logo" width="28" height="28" alt="" />
          <img :src="slicealText" alt="Sliceal" class="drawer-header__wordmark" />
        </Box>
        <Box
          is="button"
          align="center"
          justify="center"
          class="drawer-close"
          aria-label="Close menu"
          @click="close"
        >
          <PiIcon name="times" />
        </Box>
      </Box>
    </template>

    <Box direction="col" gap="3" class="drawer-body">
      <Box direction="col" gap="1" class="drawer-user">
        <Text type="body" weight="semibold">{{ displayName || 'Welcome' }}</Text>
        <Text v-if="displayEmail" type="caption" color="muted">{{ displayEmail }}</Text>
        <Box v-if="!isGuest" align="center" gap="1" class="drawer-user__kyc">
          <Box
            class="drawer-user__kyc-dot"
            :class="isKycVerified ? 'drawer-user__kyc-dot--ok' : 'drawer-user__kyc-dot--pending'"
          />
          <Text type="caption" :color="isKycVerified ? 'success' : 'warning'" weight="medium">
            {{ isKycVerified ? 'KYC verified' : 'Verify identity' }}
          </Text>
        </Box>
      </Box>

      <Box direction="col" gap="1" is="nav" class="drawer-section" aria-label="Primary">
        <RouterLink
          v-for="item in primaryItems"
          :key="item.to"
          :to="item.to"
          class="drawer-link"
          active-class="drawer-link--active"
          @click="close"
        >
          <PiIcon :name="item.icon" class="drawer-link__icon" />
          <Text type="body" weight="medium" is="span">{{ item.label }}</Text>
        </RouterLink>
      </Box>

      <Box class="drawer-divider" />

      <Box direction="col" gap="1" is="nav" class="drawer-section" aria-label="Account">
        <RouterLink
          v-for="item in secondaryItems"
          :key="item.to"
          :to="item.to"
          class="drawer-link drawer-link--muted"
          active-class="drawer-link--active"
          @click="close"
        >
          <PiIcon :name="item.icon" class="drawer-link__icon" />
          <Text type="body" is="span">{{ item.label }}</Text>
        </RouterLink>
      </Box>

      <Box class="drawer-divider" />

      <Box
        is="button"
        align="center"
        gap="2"
        class="drawer-logout"
        aria-label="Log out"
        @click="handleLogout"
      >
        <PiIcon name="sign-out" class="drawer-link__icon" />
        <Text type="body" weight="medium" color="danger" is="span">Log out</Text>
      </Box>
    </Box>
  </Drawer>
</template>

<style scoped>
.drawer-header {
  padding: 0.25rem 0;
}

.drawer-header__wordmark {
  height: 18px;
  width: auto;
}

.drawer-close {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  color: var(--p-text-muted-color);
  cursor: pointer;
  padding: 0;
}

.drawer-close:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.drawer-body {
  padding: 0.5rem 0 1.5rem;
}

.drawer-user {
  padding: 0.75rem 1rem 1rem;
  background: var(--p-surface-50);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
}

.drawer-user__kyc {
  margin-top: 0.5rem;
}

.drawer-user__kyc-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.drawer-user__kyc-dot--ok {
  background: var(--p-success-500);
}

.drawer-user__kyc-dot--pending {
  background: var(--p-warning-500);
  box-shadow: 0 0 0 3px var(--p-warning-100);
}

.drawer-section {
  display: flex;
  flex-direction: column;
}

.drawer-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-radius: var(--p-border-radius-md);
  text-decoration: none;
  color: var(--p-text-color);
  min-height: 44px;
  transition: background 0.15s ease;
}

.drawer-link__icon {
  font-size: 1rem;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
  color: var(--p-text-muted-color);
}

.drawer-link--muted {
  color: var(--p-text-muted-color);
}

.drawer-link:hover {
  background: var(--p-surface-100);
}

.drawer-link--active {
  background: var(--p-primary-50);
}

.drawer-link--active :deep(.drawer-link__icon) {
  color: var(--p-primary-700);
}

.drawer-divider {
  height: 1px;
  background: var(--p-content-border-color);
  margin: 0.25rem 0;
}

.drawer-logout {
  display: flex;
  width: 100%;
  padding: 0.75rem 0.875rem;
  border-radius: var(--p-border-radius-md);
  border: none;
  background: transparent;
  cursor: pointer;
  min-height: 44px;
  text-align: left;
}

.drawer-logout:hover {
  background: var(--p-danger-50);
}
</style>
